/**
 * FRONT-2387: turns a chat reply's Markdown into plain text a TTS engine can
 * read aloud, then splits that text into a pipelined sequence of chunks
 * short enough to synthesize and play back-to-back with a fast first sound.
 *
 * Pure and DOM-free so both halves are unit-testable without mounting
 * anything — `audio-answer-player.ts` calls `toSpeakableText`/
 * `chunkForSpeech`; `finalAnswerText` (below) is called directly by
 * `message-bubble.tsx`'s `MessageFooter` and `pwa-message.tsx`'s
 * `PwaMessage` to select which part of a multi-part reply is the one
 * actually worth converting, before it ever reaches this module's own
 * functions.
 */
import { flattenMarkdownBlocks } from '~/lib/markdown-inline';

/** Structural shape of a UI-message part — kept minimal and independent of
 *  the `ai` package's `UIMessage` type so this stays DOM/library-free. */
export interface SpeakablePart {
  type: string;
  text?: string;
  data?: unknown;
}

/** A genuine tool call/result part — the ONLY thing that marks the boundary
 *  between a preamble and the final answer (see `finalAnswerText` below).
 *  Matches the same two shapes `message-bubble.tsx`/`pwa-message.tsx` already
 *  test for when rendering a tool card: a `dynamic-tool` part, or a
 *  `tool-<name>` part (AI SDK v5's per-tool part type). */
function isToolPart(type: string): boolean {
  return type === 'dynamic-tool' || type.startsWith('tool-');
}

/**
 * FRONT-2387 fix round 1 (#10): a multi-part assistant reply — text, then a
 * tool call, then more text — has a PREAMBLE ("Let me check that...") ahead
 * of its tools and a FINAL ANSWER after them. Only the final answer is worth
 * speaking; the preamble is redundant once the tool result is already being
 * acted on, and running it together with the final answer with no separator
 * reads as one run-on sentence ("Checking.The weather is sunny."). This
 * keeps only the text parts AFTER the last TOOL part, and — when there IS
 * more than one such part — joins them with a space rather than `''`. When
 * there are no tool parts at all, every text part is the final answer, and
 * the same space-join applies.
 *
 * FRONT-2387 fix round 2 (#2): the cut point is a TOOL part only — never any
 * other non-text part. A streamed message carries `step-start` parts a
 * reloaded/persisted one doesn't (so cutting on those would give the two a
 * different final answer for identical content), and a warning data part
 * (`data-stall-warning`, `data-truth-guard-warning`, `data-fabrication-warning`,
 * `data-narrated-tool-warning`) rides AFTER the real text — cutting on that
 * emptied the answer entirely, which is the bug this closes. Slicing to the
 * cut point and then filtering to `type === 'text'` already drops any
 * intervening non-tool, non-text part (reasoning, step-start, a warning
 * part before the end) without it ever affecting where the cut itself falls.
 *
 * FRONT-2387 fix round 4 (#6): a tool call can be the reply's LAST part —
 * the model ran a tool and never wrote a follow-up sentence after it. The
 * cut-then-filter above then has nothing left to join, which would leave a
 * reply with real prose (the preamble before that trailing tool call)
 * reading as silence. When the text after the cut is empty, fall back to
 * every text part in the message, preamble included — better to speak the
 * preamble than to speak nothing. A reply with NO text parts at all (a bare
 * tool call, nothing else) still correctly falls through to ''.
 */
export function finalAnswerText(parts: readonly SpeakablePart[]): string {
  // FRONT-2601: a Continue card that replaces the message's text means that
  // text is a hidden server boilerplate sentence — never read it aloud.
  // Mirrors `hasTextReplacingContinueOffer` (chat/ordered-segments.ts), kept
  // inline so this module stays dependency-free.
  if (
    parts.some(
      (p) =>
        p.type === 'data-continue-offer' &&
        (p.data as { replacesText?: unknown } | undefined)?.replacesText === true,
    )
  ) {
    return '';
  }
  let cut = 0; // one past the index of the last TOOL part
  parts.forEach((p, i) => {
    if (isToolPart(p.type)) cut = i + 1;
  });
  const joinText = (ps: readonly SpeakablePart[]) =>
    ps
      .filter((p) => p.type === 'text')
      .map((p) => p.text ?? '')
      .join(' ')
      .trim();

  const afterCut = joinText(parts.slice(cut));
  return afterCut || joinText(parts);
}

/** `__word__`/`**word**` content that reads as a plain identifier (letters,
 *  digits, underscores, no spaces) rather than a phrase — a Python dunder
 *  like `__init__` written in prose with no backticks, not real emphasis. */
function looksLikeIdentifier(s: string): boolean {
  return /^[A-Za-z0-9_]+$/.test(s);
}

/** Common named HTML entities a reply might carry (rare, but `&amp;` shows
 *  up whenever a model echoes back already-escaped text). Anything not in
 *  this small set is left alone — better an unrecognised `&foo;` survives
 *  literally than a guess mangles real content. */
const HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  nbsp: ' ',
};

/**
 * Reduce a chat reply to speakable plain text.
 *
 * Order matters: fenced code and images are removed FIRST, while they are
 * still recognisable as whole constructs — a fenced block can itself
 * contain characters (table pipes, asterisks) that later steps would
 * otherwise misinterpret as formatting to strip rather than code to drop.
 * `flattenMarkdownBlocks` (the shared line-flattener `InlineMarkdown` also
 * uses) then does the block-level work this module doesn't have to
 * duplicate — headings, blockquote markers, BULLET list markers (rendered
 * as a spoken-safe '•', not one of the forbidden syntax characters), rules
 * and table DIVIDER rows. It has no notion of an ORDERED list marker ("1. ")
 * or an indented code block, so those are stripped here, line by line,
 * before flattening runs. What it deliberately leaves alone otherwise —
 * table pipes on ordinary rows, and every inline syntax character — is
 * stripped here too.
 */
export function toSpeakableText(markdown: string): string {
  if (!markdown) return '';

  let text = markdown;

  // Fenced code blocks dropped ENTIRELY (delimiter + contents) — a code
  // sample read character-by-character is worse than silence, and its
  // contents can carry pipes/asterisks that would otherwise confuse the
  // steps below. Handles both ``` and ~~~ fences, either well-formed pair —
  // and, when a reply was cut off mid-block, an UNCLOSED one too: everything
  // from the opening marker to the end of the text is code just the same.
  text = text.replace(/```[\s\S]*?```/g, ' ').replace(/~~~[\s\S]*?~~~/g, ' ');
  text = text.replace(/```[\s\S]*$/, ' ').replace(/~~~[\s\S]*$/, ' ');

  // Images dropped entirely (alt text included) — brief calls for removal,
  // not the alt-text fallback InlineMarkdown uses for a clickable row.
  text = text.replace(/!\[[^\]]*\]\([^)]*\)/g, ' ');

  // Links reduced to their label.
  text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');

  // Footnote AND link-reference DEFINITION lines ("[^1]: text" /
  // "[1]: https://...") are metadata that never renders in a normal reply
  // body and is never meant to be read — dropped wholesale, whole line,
  // content included. This runs BEFORE the inline reference strip below: an
  // inline "[^1]" pattern also matches the START of a definition line, and
  // stripping that first would leave the line no longer starting with "["
  // — invisible to the line-level regex, so its content ("Source text
  // here.") would leak through as if it were ordinary prose.
  text = text.replace(/^\[\^?[^\]]+\]:.*$/gm, '');
  // Footnote references ("...true[^1].") dropped inline.
  text = text.replace(/\[\^[^\]]+\]/g, '');

  // Bare URLs — http(s) and a bare "www." domain alike — removed outright;
  // no TTS voice reads one usefully aloud. The required trailing character
  // class keeps a SENTENCE-ENDING period immediately after the URL out of
  // the match: a plain greedy `\S+` would swallow it too, silently merging
  // the next sentence into this one with no boundary left to split on.
  text = text.replace(/\b(?:https?:\/\/|www\.)\S*[^\s.,!?;:'")\]]/gi, ' ');

  // Raw HTML: <br> becomes a line break (the two sides read as separate
  // thoughts, same as a real paragraph break); anything else is dropped
  // outright. A handful of named entities are decoded back to their plain
  // character rather than read as literal markup ("and amp three").
  text = text.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, ' ');
  text = text.replace(/&(amp|lt|gt|quot|nbsp);/gi, (_m, name: string) => HTML_ENTITIES[name.toLowerCase()]);

  // Ordered-list markers ("1. ", "2. ") — ONLY when followed by whitespace,
  // so a decimal like "3.5" at the start of a line is never touched.
  text = text.replace(/^\s*\d+\.\s+/gm, '');

  // Indented code blocks (4+ spaces or a tab) — dropped whole line.
  text = text.replace(/^(?: {4,}|\t).*$/gm, '');

  // Block-level flatten: headings/blockquote markers/rules/table divider
  // rows gone, one non-empty line per source line.
  text = flattenMarkdownBlocks(text);

  // Table pipes on ordinary (non-divider) rows — flattenMarkdownBlocks only
  // recognises the divider row, so a real row's cell separators survive it.
  // Safe to blank out unconditionally here: fenced code (the one place a
  // literal `|` might carry meaning, e.g. a shell pipe) is already gone.
  text = text.replace(/\|/g, ' ');

  // Inline code backticks — keep the content, drop the backtick characters.
  text = text.replace(/`+/g, '');

  // Strikethrough, then bold, then italic — longest marker first so
  // `**bold**` isn't half-eaten by the single-character italic pattern.
  text = text.replace(/~~(.+?)~~/g, '$1');
  text = text.replace(/\*\*(.+?)\*\*/g, '$1');
  // Double-underscore bold: SKIPPED (left exactly as written) when its
  // content has no space and no internal underscore — a plain identifier
  // like "init" in "__init__", a Python dunder someone wrote without
  // backticks, not a phrase meant as emphasis.
  text = text.replace(/__([^_]+?)__/g, (m, inner: string) => (looksLikeIdentifier(inner) ? m : inner));
  // Single-asterisk italics: the look-around pair keeps this from ever
  // matching a digit-flanked run like "2*3*4" — the char just outside each
  // delimiter must not itself be a word character or another asterisk, which
  // a multiplication expression's digits always are.
  text = text.replace(/(?<![\w*])\*([^\s*][^*]*?)\*(?![\w*])/g, '$1');
  text = text.replace(/\b_([^\s_][^_]*?)_\b/g, '$1');

  // A space directly before sentence punctuation is an artifact of a removed
  // inline element (a stripped URL, an emptied link) — close the gap before
  // sentence-splitting ever sees it.
  text = text.replace(/[ \t]+([.,!?;:])/g, '$1');

  // Collapse whitespace: runs of spaces/tabs on a line become one space;
  // blank lines can't occur (flattenMarkdownBlocks already drops them), but
  // collapse any that show up from the substitutions above for safety.
  text = text
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');

  return text.trim();
}

/** Sentences shorter than this are folded into a neighbour — guards against
 *  a stray fragment (a bad abbreviation split, a lone leftover glyph) ever
 *  becoming its own tiny, silence-then-blip chunk. */
const MIN_FRAGMENT_CHARS = 3;

/** Target size for a grouped (non-first) chunk. Deliberately a soft target,
 *  not a hard cap — `hardSplit` below enforces the real ceiling. */
const TARGET_CHUNK_CHARS = 400;

/** No chunk may ever exceed this many characters, even a single sentence
 *  with no punctuation in it anywhere. */
const HARD_MAX_CHARS = 1000;

/**
 * Abbreviations whose trailing period must never be read as a sentence end.
 * A single-letter token before the period (the "z", "B", "e", "g" in
 * "z. B." / "e.g.") is handled generically below, without needing an entry
 * here — this list only needs multi-letter abbreviations.
 */
const ABBREVIATIONS = new Set([
  'dr', 'mr', 'mrs', 'ms', 'prof', 'etc', 'vs', 'inc', 'ltd', 'co', 'jr', 'sr',
  'st', 'ca', 'bzw', 'ggf', 'inkl', 'exkl', 'std', 'nr', 'sog', 'u.a', 'i.e', 'e.g',
  // FRONT-2387 fix round 1 (#10, speech-cases.json): "Abs." (Absatz), "Mio."
  // (Million) and "vgl."/"usw." (vergleiche / und so weiter) all showed up
  // in the review's own failing corpus.
  'abs', 'mio', 'vgl', 'usw',
]);

/** Sentence-ending punctuation runs, only when followed by whitespace or the
 *  end of the line — this is what keeps a decimal like "3.5" intact: its
 *  period is followed by a digit, never whitespace, so it never matches. */
const SENTENCE_END_RE = /[.!?…]+(?=\s|$)/g;

/** The word immediately before a candidate sentence-ending punctuation run. */
function precedingWord(line: string, endIndex: number): string {
  const match = line.slice(0, endIndex).match(/([A-Za-zÀ-ÖØ-öø-ÿ]+)\s*$/);
  return match ? match[1].toLowerCase() : '';
}

/** Split one non-empty, already-trimmed line into sentences, guarding known
 *  abbreviations (and any single-letter token, which covers "z. B.",
 *  "e.g.", "i.e.", "u. a." and initials like "J. R. R.") so their periods
 *  never end a sentence early. */
function splitLineIntoSentences(line: string): string[] {
  const sentences: string[] = [];
  let start = 0;
  let match: RegExpExecArray | null;
  SENTENCE_END_RE.lastIndex = 0;
  while ((match = SENTENCE_END_RE.exec(line))) {
    const end = match.index + match[0].length;
    const word = precedingWord(line, match.index);
    if (word.length === 1 || ABBREVIATIONS.has(word)) continue; // not a real end
    const sentence = line.slice(start, end).trim();
    if (sentence) sentences.push(sentence);
    start = end;
  }
  const rest = line.slice(start).trim();
  if (rest) sentences.push(rest);
  return sentences;
}

/** Merge a too-short fragment into the sentence before it (or, failing that,
 *  the one after) so a stray 1-2 char remainder never ships as its own
 *  chunk-worthy "sentence". */
function mergeShortFragments(sentences: string[]): string[] {
  const merged: string[] = [];
  for (const s of sentences) {
    if (merged.length > 0 && s.length <= MIN_FRAGMENT_CHARS) {
      merged[merged.length - 1] = `${merged[merged.length - 1]} ${s}`;
    } else {
      merged.push(s);
    }
  }
  // A lone short first "sentence" (nothing yet to merge into) attaches
  // forward instead, so it's never emitted on its own as chunk 1.
  if (merged.length > 1 && merged[0].length <= MIN_FRAGMENT_CHARS) {
    merged[1] = `${merged[0]} ${merged[1]}`;
    merged.shift();
  }
  return merged;
}

/** Whether `s` has anything worth SAYING — at least one letter or digit in
 *  any script. A "sentence" that is purely punctuation/symbols/whitespace
 *  (an em dash, a stray run of asterisks left over from a stripped marker,
 *  a lone "...") has nothing to speak and must never surface as a chunk. */
function hasSpeakableContent(s: string): boolean {
  return /[\p{L}\p{N}]/u.test(s);
}

/** Exported for tests and for nothing else. */
export function splitSentences(text: string): string[] {
  const lines = text.split('\n');
  const sentences: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    sentences.push(...splitLineIntoSentences(trimmed));
  }
  return mergeShortFragments(sentences.filter(hasSpeakableContent));
}

/** Hard-split a single piece of text at word boundaries so no returned piece
 *  exceeds `maxLen` — falls back to a raw character slice only for a single
 *  word that alone is longer than `maxLen` (there's no boundary left to
 *  break on). */
function hardSplit(text: string, maxLen = HARD_MAX_CHARS): string[] {
  if (text.length <= maxLen) return [text];
  const words = text.split(/\s+/);
  const pieces: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxLen && current) {
      pieces.push(current);
      current = word;
    } else {
      current = candidate;
    }
    while (current.length > maxLen) {
      pieces.push(current.slice(0, maxLen));
      current = current.slice(maxLen);
    }
  }
  if (current) pieces.push(current);
  return pieces;
}

/** Chunk 1 grows until it holds at least this many characters. A one-word
 *  first chunk ("Sure.") is ~1 s of audio, far too little to cover the fetch
 *  of chunk 2 — the player would stall right after it (FRONT-3103). */
const MIN_FIRST_CHUNK_CHARS = 40;

/** Merge every sentence ending in ':' (a lead-in such as the "Open Source:"
 *  of a bold label line) with the sentence after it, repeatedly, with no
 *  length bound: a label then never stands alone as a tiny clip and never
 *  ends a chunk while text follows it. A merged unit over `HARD_MAX_CHARS`
 *  is cut later by `hardSplit` at word boundaries, so the label leads the
 *  first piece. A trailing lead-in with nothing after it is kept as is. */
function attachLeadIns(sentences: string[]): string[] {
  const units: string[] = [];
  let i = 0;
  while (i < sentences.length) {
    let unit = sentences[i];
    while (unit.endsWith(':') && i + 1 < sentences.length) {
      unit = `${unit} ${sentences[i + 1]}`;
      i++;
    }
    units.push(unit);
    i++;
  }
  return units;
}

/**
 * Group already-speakable text into a pipelined chunk sequence for
 * `audio-answer-player.ts`. Sentences ending in ':' are first attached to
 * the sentence that follows (`attachLeadIns`). Chunk 1 is the first unit,
 * extended one unit at a time until it holds at least `MIN_FIRST_CHUNK_CHARS`
 * (never past `HARD_MAX_CHARS`) — short enough for a fast first sound, long
 * enough to cover the next fetch. Later chunks are units grouped up to
 * `TARGET_CHUNK_CHARS`. No chunk ever exceeds `HARD_MAX_CHARS` (enforced by
 * `hardSplit`, at a word boundary wherever one exists), and no empty chunk
 * is ever returned.
 */
export function chunkForSpeech(text: string): string[] {
  const units = attachLeadIns(splitSentences(text));
  if (units.length === 0) return [];

  const chunks: string[] = [];
  let next = 1;
  if (units[0].length > HARD_MAX_CHARS) {
    chunks.push(...hardSplit(units[0]));
  } else {
    let first = units[0];
    while (
      first.length < MIN_FIRST_CHUNK_CHARS &&
      next < units.length &&
      first.length + 1 + units[next].length <= HARD_MAX_CHARS
    ) {
      first = `${first} ${units[next]}`;
      next++;
    }
    chunks.push(first);
  }

  let current = '';
  for (let i = next; i < units.length; i++) {
    const sentence = units[i];
    if (sentence.length > HARD_MAX_CHARS) {
      if (current) {
        chunks.push(current);
        current = '';
      }
      chunks.push(...hardSplit(sentence));
      continue;
    }
    const candidate = current ? `${current} ${sentence}` : sentence;
    if (candidate.length > TARGET_CHUNK_CHARS && current) {
      chunks.push(current);
      current = sentence;
    } else {
      current = candidate;
    }
  }
  if (current) chunks.push(current);

  return chunks.filter((c) => c.trim().length > 0);
}
